import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { LogisticsCategory } from '../types';
import { logisticsCategoriesAPI } from '../services/api';

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const LogisticsCategories: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [categories, setCategories] = useState<LogisticsCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LogisticsCategory | null>(null);
  const [form, setForm] = useState({ name: '', description: '', parentCategory: null as string | null });

  const parentOptions = useMemo(
    () => [
      { label: 'Bez parent kategorije', value: null },
      ...categories.map((c) => ({ label: c.name, value: c._id }))
    ],
    [categories]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await logisticsCategoriesAPI.getAll();
      setCategories(data);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || 'Greška pri učitavanju kategorija');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', parentCategory: null });
    setDialogVisible(true);
  };

  const openEdit = (row: LogisticsCategory) => {
    setEditing(row);
    setForm({ name: row.name, description: row.description || '', parentCategory: (row.parentCategory as any) || null });
    setDialogVisible(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Naziv kategorije je obavezan', life: 2500 });
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await logisticsCategoriesAPI.update(editing._id, {
          name,
          description: form.description || '',
          parentCategory: form.parentCategory || null
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija je ažurirana', life: 2500 });
      } else {
        await logisticsCategoriesAPI.create({
          name,
          description: form.description || '',
          parentCategory: form.parentCategory || null
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija je kreirana', life: 2500 });
      }
      setDialogVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Greška pri čuvanju',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const del = (row: LogisticsCategory) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati kategoriju "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await logisticsCategoriesAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija obrisana', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.message || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const actions = (row: LogisticsCategory) => (
    <div className="flex gap-2 justify-content-end">
      <Button icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => openEdit(row)} />
      <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={() => del(row)} />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Kategorije robe</h2>
          <div className="text-600 mt-1">Organizujte foldere za katalog robe u logistici</div>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj kategoriju" icon="pi pi-plus" severity="info" onClick={openAdd} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable value={categories} loading={loading} paginator rows={10} emptyMessage="Nema kategorija" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
          <Column field="name" header="Naziv" sortable />
          <Column field="description" header="Opis" />
          <Column header="Kreirano" body={(r: LogisticsCategory) => formatDate(r.createdAt)} sortable style={{ width: '160px' }} />
          <Column header="Akcije" body={actions} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni kategoriju' : 'Nova kategorija'}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '600px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={save} loading={loading} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Parent kategorija</label>
            <Dropdown
              value={form.parentCategory}
              options={parentOptions}
              onChange={(e) => setForm((p) => ({ ...p, parentCategory: e.value }))}
              appendTo="self"
              placeholder="Bez parent kategorije"
              showClear
              filter
            />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={6} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LogisticsCategories;


